import React, { useEffect, useState } from 'react';
import * as urlParse from 'url-parse';
import { notification, Row, Col, Grid, Skeleton, Card, Descriptions, Typography } from 'antd';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { default as createApiGateway } from '../../../apiGateway';
import { validateDsp } from '../dsps/utils/validations';
import { authorizedPOST, authorizedPUT } from '../../../helpers/requester';
import { dsp, user } from '../../../constants/endpoints';
import { Search } from '../../../components';

import alpha3CountryCodeList from '../../../constants/alpha3CountryCodeList.json';
import creativeSizesList from '../../../constants/creativeSizesList.json';
import osList from '../../../constants/osList.json';

import UpsertDspForm from '../../../sections/dsps/UpsertDspForm';
import { WhiteAndBlackListName, WhiteAndBlackListType } from '../../../apiGateway/whiteAndBlackLists';

const defaultDSPState = {
  partnerId: '',
  name: '',
  host: '',
  port: 80,
  path: '',
  useBanner: false,
  useNative: false,
  useVideo: false,
  usePop: false,
  callNurls: false,
  mobileWeb: false,
  inApp: false,
  desktop: false,
  videoStartTracker: false,
  instlOnly: 0,
  minTmax: 0,
  reqUserId: 0,
  useAdapter: 0,
  dailySpendLimit: 0,
  totalSpendLimit: 0,
  maxBidfloor: 0,
  onlyStoredApps: 0,
  onlyMatchedUsers: 0,
  mismatchIpFilter: 0,
  mismatchBundlesFilter: 0,
  lowSecondPrice: 0,
  adapterLearningPercent: 0,
  PMB: 0,
  PMW: 0,
  FB: 0,
  FW: 0,
  secure: false,
  margin: 0,
  limitQps: 0,
  isActive: false,
  isBlocked: false,
  includeExtraMargin: false,
  extraMargin: 0,
  note: '',
  custom: null,
  useGzip: false,
  noImpBidding: false,
  dnsResolve: false,
  inBannerVideo: false,
  videoPlacementType: 2,
  optimizer: true,
};

const parseUrl = (url) => {
  const parsedUrl = urlParse(url, true);
  let host = parsedUrl.host || '';

  if (parsedUrl.port) {
    host = host.replace(`:${parsedUrl.port}`, '');
  }

  return {
    port: parseInt(parsedUrl.port, 10) || 80,
    host: `${parsedUrl.protocol}//${host}`,
    path:
      parsedUrl.pathname +
      (_.size(parsedUrl.query)
        ? `?${_.toPairs(parsedUrl.query)
            .map(([k, v]) => `${k}=${v}`)
            .join('&')}`
        : ''),
  };
};

export default function DspPage() {
  const params = useParams();

  const dspId = params.id;
  const isCreateMode = dspId === 'new';

  const apiGateway = createApiGateway();

  const userInfo = useSelector((state) => state.login);

  const screens = Grid.useBreakpoint();
  const [loading, setLoading] = useState(true);

  const [existingDsp, setExistingDsp] = useState();
  const [state, setState] = useState({});
  const [createDSPState, createDSPSetState] = useState(defaultDSPState);
  const [whiteLists, setWhiteLists] = useState({});

  const onEnd = () => {
    setTimeout(() => {
      window.location = `/dsps/${dspId}`;
    }, 1000 * 2);
  };

  useEffect(() => {
    (async () => {
      if (isCreateMode) {
        setLoading(false);

        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-shadow
      const dsp = await apiGateway.dsps.getById(dspId);

      // eslint-disable-next-line @typescript-eslint/no-shadow
      const [user, countriesWhiteList, dspSspWhiteList, dspSizesWhiteList, dspOsWhiteList, ssps] = await Promise.all([
        apiGateway.users.getUserById(dsp.partnerId),
        apiGateway.whiteAndBlackLists.list({
          dsp_id: dsp.id,
          name: WhiteAndBlackListName.whiteListDspCountries,
          type: WhiteAndBlackListType.White,
        }),
        apiGateway.whiteAndBlackLists.list({
          dsp_id: dsp.id,
          name: WhiteAndBlackListName.whiteListDsp,
          type: WhiteAndBlackListType.White,
        }),
        apiGateway.whiteAndBlackLists.list({
          dsp_id: dsp.id,
          name: WhiteAndBlackListName.whiteListDspSizes,
          type: WhiteAndBlackListType.White,
        }),
        apiGateway.whiteAndBlackLists.list({
          dsp_id: dsp.id,
          name: WhiteAndBlackListName.whiteListDspOs,
          type: WhiteAndBlackListType.White,
        }),
        apiGateway.ssps.getAll({ includeSpend: false }),
      ]);

      setState({
        ...state,
        selectedCompany: user,
        ssps,
      });

      setExistingDsp(dsp);
      createDSPSetState(dsp);

      setWhiteLists({
        ...whiteLists,
        countriesWhiteList,
        dspSspWhiteList,
        dspSizesWhiteList,
        dspOsWhiteList,
      });

      setLoading(false);
    })();
  }, []);

  const onCompanySelect = (data) => {
    createDSPSetState({
      ...createDSPState,
      partnerId: data.id,
    });

    setState({
      ...state,
      selectedCompany: data,
    });
  };

  const createDSP = async (values) => {
    try {
      let body = { ...values, partnerId: createDSPState.partnerId };

      for (const key in body) {
        if (body.hasOwnProperty(key)) {
          if (body[key] === false) {
            body[key] = 0;
          }

          if (body[key] === true) {
            body[key] = 1;
          }
        }
      }

      if (body && body.endpointUrl) {
        body = _.omit(
          {
            ...body,
            ...parseUrl(body.endpointUrl),
          },
          'endpointUrl'
        );
      }

      body.custom = JSON.stringify(values.custom);

      validateDsp(body);

      await authorizedPOST(dsp.list, body);

      createDSPSetState(defaultDSPState);

      notification.success({ title: 'DSP created' });

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onEnd && onEnd();
    } catch (e) {
      notification.error({ title: `Error when creating a DSP: ${e}` });
    }
  };

  const updateDSP = async (values) => {
    if (!values.id) {
      notification.info({ title: 'First of all you need to fetch DSP using "Fetch DSP by ID" section' });
      return;
    }

    try {
      let body = { ...values };

      for (const key in body) {
        if (body.hasOwnProperty(key)) {
          if (body[key] === false) {
            body[key] = 0;
          }

          if (body[key] === true) {
            body[key] = 1;
          }
        }
      }

      if (body && body.endpointUrl) {
        body = _.omit(
          {
            ...body,
            ...parseUrl(body.endpointUrl),
          },
          'endpointUrl'
        );
      }

      validateDsp(body);

      await authorizedPUT(dsp.list.concat('/', createDSPState.id), body);

      createDSPSetState(defaultDSPState);

      notification.success({ title: 'DSP updated' });
    } catch (e) {
      notification.error({ title: `Error when updating a DSP: ${e}` });
    } finally {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onEnd && onEnd();
    }
  };

  const dataFilterFunc = (stateInput, data) =>
    (data || []).filter((x) => {
      if (!x || !x.firstName) return false;
      return x.firstName.toLowerCase().includes(stateInput.toLowerCase());
    });

  const allowedWhiteListCountryValues = alpha3CountryCodeList
    .map((x) => ({
      value: x.alpha3,
      name: x.country,
    }))
    .map((x) => {
      const sameFromApi = (whiteLists.countriesWhiteList || []).find((y) => y.value === x.value);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  const allowedWhiteListSspValues = (state.ssps || [])
    .map((x) => ({
      ssp_id: x.id,
      name: x.name,
      active: x.isActive ? '+' : '-',
    }))
    .map((x) => {
      const sameFromApi = (whiteLists.dspSspWhiteList || []).find((y) => y.ssp_id === x.ssp_id);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  const allowedWhiteListSizesValues = creativeSizesList
    .map((x) => ({ value: x }))
    .map((x) => {
      const sameFromApi = (whiteLists.dspSizesWhiteList || []).find((y) => y.value === x.value);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  const allowedWhiteListOsValues = osList
    .map((x) => ({ value: x }))
    .map((x) => {
      const sameFromApi = (whiteLists.dspOsWhiteList || []).find((y) => y.value === x.value);

      return {
        __toggle: Boolean(sameFromApi),
        ...x,
        id: (sameFromApi && sameFromApi.id) || null,
      };
    });

  if (loading) {
    return (
      <Row>
        <Col xs={24} md={{ span: 20, offset: 2 }}>
          <Skeleton active />
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col xs={24} md={{ span: 20, offset: 2 }}>
        {state.selectedCompany && createDSPState.partnerId ? (
          <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
            <Descriptions
              size="small"
              column={1}
              layout={screens.md ? 'horizontal' : 'vertical'}
              styles={{ label: { width: screens.md ? 120 : 'auto' } }}
            >
              <Descriptions.Item label="Company">
                [{state.selectedCompany.id}] {state.selectedCompany.firstName}
              </Descriptions.Item>

              {existingDsp && (
                <Descriptions.Item label="DSP">
                  [{existingDsp.id}] {existingDsp.name || '-'}
                </Descriptions.Item>
              )}

              {existingDsp?.statUrl && (
                <Descriptions.Item label="Stats URL">
                  <Typography.Paragraph
                    style={{ marginBottom: 0 }}
                    copyable={{ text: existingDsp.statUrl }}
                    ellipsis={{ rows: 1, tooltip: existingDsp.statUrl }}
                  >
                    <a href={existingDsp.statUrl} target="_blank" rel="noreferrer">
                      {existingDsp.statUrl}
                    </a>
                  </Typography.Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        ) : (
          <div style={{ marginTop: 16 }}>
            <Search
              title="Select Company"
              placeholder="Enter company name"
              baseUrl={user.root}
              onSelect={onCompanySelect}
              dataFilterFunc={dataFilterFunc}
            />
          </div>
        )}

        <UpsertDspForm
          dsp={existingDsp}
          userInfo={userInfo}
          onSubmit={createDSPState.id ? updateDSP : createDSP}
          allowedWhiteListCountryValues={allowedWhiteListCountryValues}
          allowedWhiteListSspValues={allowedWhiteListSspValues}
          allowedWhiteListSizesValues={allowedWhiteListSizesValues}
          allowedWhiteListOsValues={allowedWhiteListOsValues}
        />
      </Col>
    </Row>
  );
}
